import { CircuitBreakerAuthService } from "@ms/uno-services/lib/local/services/auth/CircuitBreakerAuthService";
import { HostAdaptorAuthService } from "@ms/uno-services/lib/local/services/auth/HostAdaptorAuthService";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/** Implementation of the service provider factory for the Base app */
export class BaseServiceFactory {
    /**
     * Initialize the service factory
     * @param loader Component loader
     * @param loggers Loggers for telemetry
     */
    constructor(loader, loggers) {
        this.loader = loader;
        this.loggers = loggers;
    }
    getAdaptiveCardServiceProvider(ajaxClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("adaptiveCard"), () => import(
        /* webpackChunkName: "uno-adaptivecardservice" */ "@ms/uno-services/lib/local/services/mtrs/adaptiveCard/AdaptiveCardService"), (bundle) => new bundle.AdaptiveCardService(ajaxClient, configProvider));
    }
    getAugloopServiceProvider(authTokenProvider, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("al"), () => import(/* webpackChunkName: "uno-alservice" */ "@ms/uno-services/lib/local/services/augloop/AugLoopService"), (bundle) => new bundle.AugLoopService(true, this.loggers, authTokenProvider, configProvider));
    }
    getAuthServiceProvider(ajaxClient, hostAdaptor, configProvider) {
        return () => {
            if (this.auth) {
                return this.auth;
            }
            const realAuth = new HostAdaptorAuthService(this.loggers, hostAdaptor, configProvider);
            this.auth = new CircuitBreakerAuthService(realAuth, configProvider, this.loggers);
            return this.auth;
        };
    }
    getCentroServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("centro"), () => import(/* webpackChunkName: "uno-centroservice" */ "@ms/uno-services/lib/local/services/centro/CentroRequestAdapter"), (bundle) => new bundle.CentroRequestAdapter(requestClient, this.loggers, configProvider));
    }
    getChatSvcAggServiceProvider(requestClient, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1e0dd29a /* tag_4d3k0 */, TraceLevel.Error, `Shouldn't import new ChatSvcAggService for Uno.`);
            throw new Error("NotSupported");
        };
    }
    getCopilotServiceProvider(requestClient, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1d79c097 /* tag_342cx */, TraceLevel.Error, `Shouldn't import CopilotService for Uno.`);
            throw new Error("NotSupported");
        };
    }
    getLegacyChatSvcAggServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("legacychatsvcagg"), () => import(
        /* webpackChunkName: "uno-legacychatsvcaggservice" */ "@ms/uno-services/lib/local/services/legacy/chatsvcagg/ChatSvcAggService"), (bundle) => new bundle.ChatSvcAggService(requestClient, configProvider));
    }
    getLegacyTcmsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("legacytcms"), () => import(/* webpackChunkName: "uno-legacytcmsservice" */ "@ms/uno-services/lib/local/services/tcms/legacy/TcmsService"), (bundle) => new bundle.TcmsService(requestClient, configProvider));
    }
    getGroupsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("groups"), () => import(/* webpackChunkName: "uno-groupsservice" */ "@ms/uno-services/lib/local/services/graph/groups/GroupsService"), (bundle) => new bundle.GroupsService(requestClient, configProvider));
    }
    getModernGroupServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("moderngroup"), () => import(
        /* webpackChunkName: "uno-moderngroupservice" */ "@ms/uno-services/lib/local/services/graph/modernGroup/ModernGroupService"), (bundle) => new bundle.ModernGroupService(requestClient, configProvider));
    }
    getIrisServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("iris"), () => import(/* webpackChunkName: "uno-irisservice" */ "@ms/uno-services/lib/local/services/iris/IrisService"), (bundle) => new bundle.IrisService(requestClient, this.loggers, configProvider));
    }
    getLoopServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("loop"), () => import(/* webpackChunkName: "uno-loopservice" */ "@ms/uno-services/lib/local/services/loop/LoopService"), (bundle) => new bundle.LoopService(requestClient, configProvider));
    }
    getLoopMtsServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("loopmts"), () => import(/* webpackChunkName: "uno-loopmtsservice" */ "@ms/uno-services/lib/local/services/loop/LoopMtsService"), (bundle) => new bundle.LoopMtsService(requestClient, configProvider));
    }
    getMarsServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("mars"), () => import(/* webpackChunkName: "uno-marsservice" */ "@ms/uno-services/lib/local/services/mars/MarsService"), (bundle) => new bundle.MarsService(requestClient, configProvider));
    }
    getMruServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("mru"), () => import(/* webpackChunkName: "uno-mruservice" */ "@ms/uno-services/lib/local/services/mru/MruService"), (bundle) => new bundle.MruService(requestClient, this.loggers, configProvider));
    }
    getOcpsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("ocps"), () => import(/* webpackChunkName: "uno-ocpsservice" */ "@ms/uno-services/lib/local/services/ocps/OcpsService"), (bundle) => new bundle.OcpsService(requestClient, configProvider));
    }
    getOneShellServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1e3196c3 /* tag_4mz1d */, TraceLevel.Error, `Shouldn't import oneShell service for base (non-Web) endpoint.`);
            throw new Error("NotSupported");
        };
    }
    getPlannerGraphServiceProvider(requestClient, requestPrioritizer, configProvider, isAppInBackground) {
        return () => this.loader.load(this.getServiceBundleKey("plannerGraph"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedplannergraphservice" */ "@ms/uno-services/lib/local/services/graph/planner/GatedPlannerService"),
            import(
            /* webpackChunkName: "uno-plannergraphservice" */ "@ms/uno-services/lib/local/services/graph/planner/PlannerService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedPlannerService(new api.PlannerService(requestClient, configProvider, isAppInBackground), requestPrioritizer, this.loggers));
    }
    getPlannerServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("planner"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedplannerservice" */ "@ms/uno-services/lib/local/services/planner/GatedPlannerService"),
            import(/* webpackChunkName: "uno-plannerservice" */ "@ms/uno-services/lib/local/services/planner/PlannerService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedPlannerService(new api.PlannerService(requestClient, configProvider, this.loggers), requestPrioritizer, this.loggers));
    }
    getLegacyPlpServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("plp"), () => import(
        /* webpackChunkName: "uno-legacyplpservice" */ "@ms/uno-services/lib/local/services/project/legacy/ProjectLandingPageService"), (bundle) => new bundle.ProjectLandingPageService(requestClient, configProvider));
    }
    getProjectLandingPageServiceProvider(requestClient, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1e09031b /* tag_4cqm1 */, TraceLevel.Error, `Shouldn't import new ProjectLandingService for Uno.`);
            throw new Error("NotSupported");
        };
    }
    getProjectSchedulingServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("pss"), () => import(/* webpackChunkName: "uno-pssservice" */ "@ms/uno-services/lib/local/services/project/ProjectSchedulingService"), (bundle) => new bundle.ProjectSchedulingService(requestClient, this.loggers, configProvider));
    }
    getRoamingServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("roaming"), () => import(/* webpackChunkName: "uno-roamingservice" */ "@ms/uno-services/lib/local/services/roaming/RoamingService"), (bundle) => new bundle.RoamingService(requestClient, configProvider));
    }
    getSiteURLServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("siteurl"), () => import(/* webpackChunkName: "uno-siteurlservice" */ "@ms/uno-services/lib/local/services/graph/siteURL/SiteURLService"), (bundle) => new bundle.SiteURLService(requestClient, configProvider));
    }
    getSubstrateSearchServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("substratesearch"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedsubstratesearchservice" */ "@ms/uno-services/lib/local/services/3S/GatedSubstrateSearchService"),
            import(
            /* webpackChunkName: "uno-substratesearchservice" */ "@ms/uno-services/lib/local/services/3S/SubstrateSearchService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedSubstrateSearchService(new api.SubstrateSearchService(requestClient, this.loggers, configProvider), requestPrioritizer, this.loggers));
    }
    getTcmsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1d88c68b /* tag_38m0l */, TraceLevel.Error, `Shouldn't import new TCMS service for UNO app.`);
            throw new Error("NotSupported");
        };
    }
    getTeamsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("teams"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedteamsservice" */ "@ms/uno-services/lib/local/services/graph/teams/GatedTeamsService"),
            import(/* webpackChunkName: "uno-teamsservice" */ "@ms/uno-services/lib/local/services/graph/teams/TeamsService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedTeamsService(new api.TeamsService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getConversationsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("conversations"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedconversationsservice" */ "@ms/uno-services/lib/local/services/graph/conversations/GatedConversationsService"),
            import(
            /* webpackChunkName: "uno-conversationsservice" */ "@ms/uno-services/lib/local/services/graph/conversations/ConversationsService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedConversationsService(new api.ConversationsService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getDriveItemServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("driveitem"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gateddriveitemservice" */ "@ms/uno-services/lib/local/services/graph/driveItem/GatedDriveItemService"),
            import(
            /* webpackChunkName: "uno-driveitemservice" */ "@ms/uno-services/lib/local/services/graph/driveItem/DriveItemService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedDriveItemService(new api.DriveItemService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getEventsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1d7d821b /* tag_35yi1 */, TraceLevel.Error, "getEventsServiceProvider is not supported in non-converged app");
            throw new Error("getEventsServiceProvider is not supported in non-converged app");
        };
    }
    getFlowServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("flow"), () => import(/* webpackChunkName: "uno-flowservice" */ "@ms/uno-services/lib/local/services/flow/FlowService"), (bundle) => new bundle.FlowService(requestClient, configProvider));
    }
    getGatewayServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("gateway"), () => import(
        /* webpackChunkName: "uno-gatewayservice" */ "@ms/uno-services/lib/local/services/project/gatewayService/GatewayService"), (bundle) => new bundle.GatewayService(requestClient, configProvider));
    }
    getTenantServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("tenant"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedtenantservice" */ "@ms/uno-services/lib/local/services/graph/tenant/GatedTenantService"),
            import(
            /* webpackChunkName: "uno-tenantservice" */ "@ms/uno-services/lib/local/services/graph/tenant/TenantService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedTenantService(new api.TenantService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getPlannerTenantServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return async () => {
            this.loggers.traceLogger.logTrace(0x1e0d8580 /* tag_4dywa */, TraceLevel.Error, `Shouldn't import new PlannerTenantService for Uno.`);
            throw new Error("NotSupported");
        };
    }
    getRetailServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("retail"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedretailservice" */ "@ms/uno-services/lib/local/services/retail/GatedRetailService"),
            import(/* webpackChunkName: "uno-retailservice" */ "@ms/uno-services/lib/local/services/retail/RetailService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedRetailService(new api.RetailService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getTodoServiceProvider(requestClient, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("todo"), () => import(/* webpackChunkName: "uno-todoservice" */ "@ms/uno-services/lib/local/services/todo/TodoService"), (bundle) => new bundle.TodoService(requestClient, configProvider));
    }
    getPublishingServiceProvider(authTokenProvider, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("publishing"), () => import(
        /* webpackChunkName: "uno-publishingservice" */ "@ms/uno-services/lib/local/services/publishing/PublishingService"), (bundle) => new bundle.PublishingService(this.loggers, authTokenProvider, configProvider));
    }
    getUserServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("user"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gateduserservice" */ "@ms/uno-services/lib/local/services/graph/user/GatedUserService"),
            import(/* webpackChunkName: "uno-userservice" */ "@ms/uno-services/lib/local/services/graph/user/UserService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedUserService(new api.UserService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getSearchServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("search"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedsearchservice" */ "@ms/uno-services/lib/local/services/graph/search/GatedSearchService"),
            import(
            /* webpackChunkName: "uno-searchservice" */ "@ms/uno-services/lib/local/services/graph/search/SearchService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedSearchService(new api.SearchService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getMsTeamsServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("msteams"), () => Promise.all([
            import(
            /* webpackChunkName: "uno-gatedmsteamsservice" */ "@ms/uno-services/lib/local/services/msTeams/GatedMsTeamsService"),
            import(/* webpackChunkName: "uno-msteamsservice" */ "@ms/uno-services/lib/local/services/msTeams/MsTeamsService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedMsTeamsService(new api.MsTeamsService(requestClient, configProvider), requestPrioritizer, this.loggers));
    }
    getOdspServiceProvider(requestClient, requestPrioritizer, configProvider) {
        return () => this.loader.load(this.getServiceBundleKey("odsp"), () => Promise.all([
            import(/* webpackChunkName: "uno-gatedodspservice" */ "@ms/uno-services/lib/local/services/odsp/GatedOdspService"),
            import(/* webpackChunkName: "uno-odspservice" */ "@ms/uno-services/lib/local/services/odsp/OdspService"),
        ]), ([gatedApi, api]) => new gatedApi.GatedOdspService(new api.OdspService(requestClient), requestPrioritizer, this.loggers));
    }
    getTrouterServiceProvider(getAccessTokenDelegate, refreshAccessTokenDelegate, loggers, configProvider) {
        throw new Error("NotSupported");
    }
    /**
     * Get the service bundle key for the given service name
     * @param serviceName
     */
    getServiceBundleKey(serviceName) {
        return `uno-${serviceName}service`;
    }
}
